import { http, legacyHttp } from '@noor/api';
import { env } from '@noor/config';
import {
  legacyProfileSchema,
  legacyStatsSchema,
  legacyInvoicesResponseSchema,
  integrationTokenSchema,
  type LegacyProfile,
  type LegacyStatPoint,
  type LegacyInvoicesResponse,
  type IntegrationToken,
} from '../model/schema';
import {
  legacyPricingSchema,
  legacyRatingsSchema,
  legacyCardsSchema,
  type LegacyPricing,
  type LegacyRatings,
  type LegacyCard,
} from '../model/pricing-schema';

/** Day-bucketed stats query for the "Peak Days" chart. */
export interface LegacyStatsQuery {
  /** Inclusive start, `YYYY-MM-DD`. */
  fromDate: string;
  /** Inclusive end, `YYYY-MM-DD`. */
  tillDate: string;
}

export const legacyProfileApi = {
  /**
   * `newClientsFrom` (`YYYY-MM-DD`) scopes the profile's `new_client_count` —
   * the old shell always sent the current week's start, so the dashboard tile
   * means "new clients this week". Omitting it lets the backend pick its own
   * default period, which is NOT the same number.
   */
  async me(query?: { newClientsFrom?: string }): Promise<LegacyProfile> {
    const raw = await legacyHttp.get<unknown>(
      'vendors/me',
      query?.newClientsFrom ? { new_clients_from: query.newClientsFrom } : undefined,
    );
    return legacyProfileSchema.parse(raw);
  },

  async stats(query: LegacyStatsQuery): Promise<LegacyStatPoint[]> {
    // Returns a BARE ARRAY, not an envelope — and omits days with no orders,
    // so the chart backfills the missing weekdays itself.
    const raw = await legacyHttp.get<unknown>('vendors/stats', {
      from_date: query.fromDate,
      till_date: query.tillDate,
      type: 'daily',
    });
    return legacyStatsSchema.parse(raw) ?? [];
  },

  async pricing(): Promise<LegacyPricing> {
    const raw = await legacyHttp.get<unknown>('vendors/pricing');
    return legacyPricingSchema.parse(raw);
  },

  /**
   * Paginated when the caller passes `limit`/`skip` (the full `/reviews`
   * page); unpaginated for the dashboard card, which reads the backend
   * default — the old `Reviews.tsx` sent `{}` and the page sent
   * `{limit, skip}` to the same endpoint.
   */
  async ratings(query?: { limit?: number; skip?: number }): Promise<LegacyRatings> {
    const raw = await legacyHttp.get<unknown>('ratings', {
      ...(query?.limit !== undefined ? { limit: query.limit } : {}),
      ...(query?.skip !== undefined ? { skip: query.skip } : {}),
    });
    return legacyRatingsSchema.parse(raw);
  },

  async cards(): Promise<LegacyCard[]> {
    const raw = await legacyHttp.get<unknown>('vendors/cards');
    return legacyCardsSchema.parse(raw).data ?? [];
  },

  async updateProfile(data: { name: string; logo_url: string }): Promise<unknown> {
    return http.patch('/api/v1/merchants/me', data, {
      baseURL: env.VITE_CORE_API_URL,
    });
  },

  async updateCallback(data: { url: string; token: string }): Promise<unknown> {
    return legacyHttp.patch('vendors/callback', data);
  },

  /**
   * Mints a new integration token, revoking the previous one. The backend
   * answers with the token itself on success, or `{ message }` on failure
   * (surfaced as `ApiError.message` by `legacyHttp`).
   */
  async generateIntegrationToken(): Promise<IntegrationToken> {
    const raw = await legacyHttp.post<unknown>('vendors/integration/generate-token');
    return integrationTokenSchema.parse(raw);
  },

  async invoices(query: {
    limit: number;
    skip: number;
  }): Promise<LegacyInvoicesResponse> {
    const raw = await legacyHttp.get<unknown>('vendors/invoices', query);
    return legacyInvoicesResponseSchema.parse(raw);
  },
};
